import { Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, Title } from '@angular/platform-browser';

import moment from 'moment-timezone';

import { BriefingReportService } from './briefing.report.service';
import { BriefingModel } from '../../models/briefing.model';
import { BriefingsService } from '../../services/briefings.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { Subscription, combineLatest, filter, mergeMap } from 'rxjs';
import { StatisticsReportType } from '../../../../Common/TargetingShared/enum/statistics-report-type.enum';
import { MonitoringRoutingService } from '../../../Core/services/monitoring-routing.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { AttributeModel } from '../../../../Common/Misc/models/attribute.model';
import { SentBriefingEnum } from '../../enums/sent-briefing-attributes.enum';
import { PlainTextService } from '../../../../Common/PageBuilder/services/plain-text.service';
import { StatisticsReportService } from '../../../../Common/TargetingShared/services/statistics.report.service';
import { DistributionReportStat } from '../../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import { EmailRecipientSearchService } from './RecipientSearch/email-recipient-search.service';
import { StatTypeChangeInterface } from '../../../../Common/TargetingShared/model/statisticsReport/stat-type-change.interface.model';
import { IndividualRecipientReportDataModel } from '../../../../Common/TargetingShared/components/statisticReport/RecipientReport/recipient.report.data.model';
import { StatisticReportSourceType } from '../../../../Common/TargetingShared/model/statisticsReport/statistic-report-source-type.enum';
import { BriefingStatusEnum } from '@Common/MonitorShared/enum/briefing-status.enum';
import { BriefingsApiErrorEnum } from '@api/briefings/enum/briefings-api-error.enum';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { ConfirmDialogComponent } from '@Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '@Common/Dialogs/models/confirm-dialog.model';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { BriefingDeliveryMethodEnum } from '../../enums/briefing-delivery-method.enum';
import { copy } from '../../../../Common/Misc/helpers/copy/copy.helper';
import { MessageService } from 'primeng/api';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { ScrollPanelComponent } from '@Common/Basic/components/ScrollPanel/scroll-panel.component';
import { BriefingGeneratingPdfDialogComponent } from '../BriefingGeneratingPdfDialog/briefing-generating-pdf-dialog.component';

export enum BriefingReportRHPType {
    recipients,
    details
}

@Component({
    selector: 'agpr-briefing-report',
    templateUrl: './briefing.report.component.html',
    styleUrls: ['./briefing.report.component.scss'],
    providers: [
        EmailRecipientSearchService,
        {
            provide: StatisticsReportService,
            useClass: BriefingReportService
        }
    ],
    standalone: false
})
export class BriefingReportComponent implements OnDestroy {
    readonly statisticsReportType = StatisticsReportType;
    readonly buttonTypes = ButtonTypesEnum;
    readonly statisticReportSourceType = StatisticReportSourceType;
    readonly briefingDeliveryMethodEnum = BriefingDeliveryMethodEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    @ViewChild('rssFeedSentDetails') rssFeedSentDetails: TemplateRef<any>;
    @ViewChild('scrollPanel') scrollPanel: ScrollPanelComponent;

    id: string = 'briefingReport';
    public briefing: BriefingModel;
    private subscriptions: Subscription[] = [];
    routeId: number;

    selectedTabIndex: StatisticsReportType = StatisticsReportType.performance;
    selectedRHPTabIndex: number = BriefingReportRHPType.recipients;
    showExportButton: boolean;

    enableMppEstimates: boolean = false;
    sentDateValue: Date;
    dataNotAvailable: boolean = false;
    isScheduled: boolean = false;
    disableRecipientsTab: boolean = false;

    reportLoading: boolean = false;
    briefingLoading: boolean = false;
    isRSSFeed: boolean = false;
    briefingDetails: AttributeModel[] = [];
    sentDetails: AttributeModel[] = [];
    notificationDetails: AttributeModel[] = [];
    scheduledBriefingDetails: AttributeModel[] = [];
    sentDate: string = '';
    bodyHtml: string = '';
    bodyPlainText: string = '';
    selectedRecipientType: DistributionReportStat = DistributionReportStat.SENT;
    selectedIndividualRecipient: IndividualRecipientReportDataModel;
    recipientTotal: number;
    canvasUrl: string = '';

    // PDF stuff
    iframeUrl: SafeResourceUrl | null = null;
    iframeElement: HTMLIFrameElement;
    generatingPDF: boolean = false;
    includeMentionsInPDF: boolean = false;

    /**
     * Class constructor
     */
    constructor(
        public briefingsService: BriefingsService,
        public briefingReportService: StatisticsReportService,
        public localizationService: LocalizationService,
        public monitoringRoutingService: MonitoringRoutingService,
        private plainTextService: PlainTextService,
        private emailRecipientSearchService: EmailRecipientSearchService,
        private coreDialogService: CoreDialogService,
        private currentUserStateService: CurrentUserStateService,
        private messageService: MessageService,
        private configService: ConfigService,
        private sanitizer: DomSanitizer,
        public titleService: Title
    ) {
        this.setupSubscription();
    }

    setupSubscription(): void {
        this.subscriptions.push(
            this.briefingReportService.loading$.subscribe(x => {
                requestAnimationFrame(() => {
                    this.reportLoading = x;
                });
            }),
            this.monitoringRoutingService.getCurrentRoute().subscribe(route => this.routeId = Number(route?.value)),
            this.briefingsService.loading$.subscribe(x => this.briefingLoading = x),
            combineLatest([
                this.briefingsService.getFocusedResource().pipe(filter(x => !!x)),
                this.currentUserStateService.getPreferences().pipe(filter(x => !!x)) // we need user prefs to load first for scheduledBriefingDetails
            ]).pipe(
                mergeMap(([briefing, userPrefs]) => {
                    if (this.briefing && this.briefing.id !== briefing.id) {
                        if (this.scrollPanel) {
                            // Reset scroll panel to top
                            this.scrollPanel.scrollTo();
                        }
                    }
                    this.briefing = briefing;
                    if (this.briefing) {
                        this.titleService.setTitle(
                            this.localizationService.transform(
                                briefing.deliveryMethod === BriefingDeliveryMethodEnum.EMAIL ?
                                    'Monitoring.briefings.browserTitle.name' : 'Monitoring.canvas.browserTitle.name',
                                { name: briefing.name }
                            )
                        );
                    }
                    this.isScheduled = this.briefing?.sendAt?.sendLater &&
                        (this.briefing.status === BriefingStatusEnum.SCHEDULED);

                    this.setBriefingDetails();
                    this.isRSSFeed = this.briefing?.selectedDeliveryOption?.rssFeed && !this.briefing?.selectedDeliveryOption?.email;

                    if (this.isScheduled) {
                        this.sentDateValue = moment(this.briefing?.sendAt?.sendLater).toDate();
                        this.selectedRHPTabIndex = BriefingReportRHPType.details;
                        this.disableRecipientsTab = true;
                    } else {
                        if (this.briefing?.sendAt?.sendNow &&
                            (this.briefing.status === BriefingStatusEnum.SCHEDULED)
                        ) {
                            this.selectedRHPTabIndex = BriefingReportRHPType.details;
                            this.disableRecipientsTab = true;
                        } else {
                            this.selectedRHPTabIndex = BriefingReportRHPType.recipients;
                            this.disableRecipientsTab = false;
                        }
                    }

                    return this.briefingsService.getSentDetails$();
                })
            ).subscribe(sentDetails => {
                if (sentDetails) {
                    sentDetails.forEach(item => {
                        if (item.id === SentBriefingEnum.rssFeeds) {
                            item.customTemplate = this.rssFeedSentDetails;
                        }

                        if (item.id === SentBriefingEnum.dateSent) {
                            this.sentDate = item.value;
                        }
                    });
                    this.sentDetails = [];
                    this.notificationDetails = [];
                    if (this.briefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                        // For canvas, separate notification details from sent details
                        sentDetails.forEach(detail => {
                            if (detail.id === SentBriefingEnum.fromName || detail.id === SentBriefingEnum.replyTo) {
                                this.notificationDetails.push(detail);
                            } else if (![SentBriefingEnum.recipients, SentBriefingEnum.rssFeeds].includes(detail.id)) {
                                this.sentDetails.push(detail);
                            }
                        });
                    }
                }
            }),
            this.briefingsService.getBriefingHistory().subscribe(history => {
                if (history?.html) {
                    this.bodyHtml = history.html;
                    const bodyArray = /<body.*?>([\s\S]*)<\/body>/.exec(history.html);
                    const bodyHtml = !!bodyArray?.length ? bodyArray[1] : history.html;
                    const span = document.createElement('span');
                    span.innerHTML = bodyHtml;
                    this.bodyPlainText = this.plainTextService.parseHTML(bodyHtml)
                        .trim()
                        .replace(/\(\)/g, '')
                        .replace(/   +/g, '<br>');
                } else {
                    this.bodyHtml =  '';
                    this.bodyPlainText =  '';
                }

                if (history?.date) {
                    this.sentDateValue = moment(history.date).toDate();
                    const expiryDate = moment().subtract(90, 'days');
                    // timestamp 1687903200000 is 6-1 release time: Tuesday, June 27, 2023 6:00:00 PM GMT-04:00
                    // For briefings sent before this date, the report data will not be available
                    // This is only a temp solution. With time, all briefings would have report data
                    this.dataNotAvailable = moment(history.date).isBefore(moment(1687903200000)) || moment(history.date).isBefore(expiryDate);
                }
            }),
            this.briefingReportService.recipientReportData$.pipe().subscribe(x => {
                this.showExportButton = !!x && x.deliveredCount > 0;
            }),
            this.emailRecipientSearchService.getItemCount().subscribe(x => this.recipientTotal = x),
            this.briefingReportService.resourceUuid$.subscribe(uuid => {
                if (uuid) {
                    this.canvasUrl = `${this.configService.getConfig().bmieUrl}briefings/${uuid}`;
                    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.canvasUrl);

                    this.iframeElement = document.getElementById('briefing_pdf_iframe') as HTMLIFrameElement;
                } else {
                    this.canvasUrl = '';
                    this.iframeUrl = null;
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    tabChange(index: StatisticsReportType) {
        this.selectedTabIndex = index;
        if (this.selectedTabIndex === StatisticsReportType.recipients) {
            this.selectedRHPTabIndex = BriefingReportRHPType.recipients;
        }
    }

    exportReport() {
        this.briefingReportService.downloadReport(this.briefing.name);
    }

    /**
     * Sets the attributes for briefing details attribute list
     */
    public setBriefingDetails(): void {
        if (this.isScheduled) {
            this.scheduledBriefingDetails = this.briefingsService.setScheduledDetails(this.isScheduled) || [];
            this.scheduledBriefingDetails.forEach(item => {
                if (item.id === SentBriefingEnum.rssFeeds) {
                    item.customTemplate = this.rssFeedSentDetails;
                }
            });
        }

        if (!this.briefing) {
            this.briefingDetails = [];
            return;
        }

        this.briefingDetails = this.briefingsService.getBriefingDetails();
    }

    changeMppEstimates(mppEstimate: boolean): void {
        this.briefingReportService.setMppEstimates(mppEstimate);
    }

    statTypeSelected(statTypeChange: StatTypeChangeInterface) {
        this.selectedIndividualRecipient = statTypeChange.data;
        this.selectedRHPTabIndex = BriefingReportRHPType.recipients;
        this.selectedRecipientType = statTypeChange.statType;
        this.emailRecipientSearchService.setStatType(this.selectedRecipientType);
        this.emailRecipientSearchService.setSelectedIndividualRecipient(statTypeChange.data);
    }

    cancel(): void {
        this.briefingsService.cancelCurrentBriefing().subscribe({
            error: (error) => {
                let errorMessage = 'Briefing.cancelBriefingErrorMessage.line2.generic';
                if (error && error.error && error.error.details) {
                    for (const errorDetail of error.error.details) {
                        if (errorDetail.code === BriefingsApiErrorEnum.tooLateToCancel) {
                            errorMessage = 'Briefing.cancelBriefingErrorMessage.line2.tooLate';
                            break;
                        }
                    }
                }
                this.openCancelErrorDialog(errorMessage);
                this.briefingsService.updateBriefingListItems();
            }
        });
    }

    openCancelErrorDialog(text: string): void {
        this.coreDialogService.open(
            ConfirmDialogComponent,
            <ConfirmDialogModel>{
                inputs: {
                    heading: 'Briefing.cancelBriefingErrorHeading',
                    okLabel: 'Briefing.cancelBriefingErrorMessage.okButtonLabel',
                    showCancel: false,
                    width: 350,
                    alertType: messageSeverityEnum.error,
                    alertMessage: {
                        key: 'Briefing.cancelBriefingErrorMessage.line1',
                        args: { name: this.briefing.name }
                    },
                    message: text
                },
                outputs: {
                    dialogClose: () => this.briefingsService.refreshCurrentBriefing()
                }
            }
        );
    }

    copyUrl(event: Event, url: string): void {
        if (!url) {
            return;
        }
        copy.text(url);
        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('Briefings.reservedUrl.noUrlAvailable.message.summary'),
            detail: this.localizationService.transform('Briefings.reservedUrl.noUrlAvailable.message.detail'),
        });
        (event.target as HTMLElement).focus();
    }

    generatePDF() {
        if (!this.iframeElement) {
            this.iframeElement = document.getElementById('briefing_pdf_iframe') as HTMLIFrameElement;
        }
        this.coreDialogService.open(
            BriefingGeneratingPdfDialogComponent,
            {
                inputs: {
                    iframeElement: this.iframeElement,
                    isReportBriefing: true,
                    includeMentionsInPDF: this.includeMentionsInPDF
                }
            }
        );
        this.generatingPDF = false;
    }

    digestChange(): void {
        this.briefingsService.patchDigestChanges(this.briefing.id).subscribe((x) => {
            if (x) {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Briefing.report.digest.update.heading'),
                    detail: this.localizationService.transform('Briefing.report.digest.update.message'),
                });
            }
        });
    }
}
